import { useEffect, useState } from 'react';
import { fleetApi } from '../api/fleet';
import { getDriverSession } from './driverSession';

/**
 * The signed-in rider's part in a delivery company: `owner`, `rider` or null
 * (InGo ADR 0032). The bottom bar shows Fleet to an owner only: a fleet's
 * riders see their own figures, never the others' (the old app opened the
 * whole fleet to anyone with a company).
 *
 * Asked of the server once per app process and kept for the tab, keyed by
 * the rider, so the bar does not ask on every screen; opening a fleet or
 * leaving one says so at once (`rememberFleetRole`).
 */
const KEY = 'ingo_driver_fleet_role';

let asked = '';

function riderId() {
  return String(getDriverSession()?.id ?? '');
}

export function readFleetRole() {
  try {
    const kept = JSON.parse(sessionStorage.getItem(KEY) || 'null');

    return kept && kept.rider === riderId() ? kept.role : null;
  } catch {
    return null;
  }
}

export function rememberFleetRole(role) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ rider: riderId(), role: role ?? null }));
  } catch {
    // A private window without storage: the bar asks again next time.
  }
}

/** The role, asked of the server the first time this process needs it. */
export function useFleetRole(fleet = fleetApi) {
  const [role, setRole] = useState(readFleetRole);

  useEffect(() => {
    const id = riderId();

    if (id === '' || asked === id) return undefined;
    asked = id;
    let cancelled = false;

    fleet.me('today').then(
      (answer) => {
        rememberFleetRole(answer?.role ?? null);
        if (!cancelled) setRole(answer?.role ?? null);
      },
      () => {
        // No signal or no session: keep what was known; the next process asks again.
        asked = '';
      },
    );

    return () => {
      cancelled = true;
    };
  }, [fleet]);

  return role;
}

/** For tests: forget that this process asked. */
export function forgetFleetRoleAsked() {
  asked = '';
}
